import React, { useState } from "react";
import cns from "classnames";
import Badge from "@material-ui/core/Badge";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import ExpandLess from "react-icons/lib/md/expand-less";
import ExpandMore from "react-icons/lib/md/expand-more";
import { ThemeType, mixins } from "@theme";
import { SubMenu } from "./SubMenu";
import { MenuItem } from "./types";
import { makeStyles } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { NavLink } from "react-router-dom";

type Props = {
  items: MenuItem[];
  onItemClick: (item: MenuItem) => void;
};

export function Menu(props: Props) {
  const [openedItems, setOpenedItems] = useState([]);

  const toggle = (id: string) => {
    setOpenedItems(
      openedItems.includes(id)
        ? openedItems.filter((x) => x !== id)
        : [...openedItems, id]
    );
  };

  const classes = useStyles({});
  const { items, onItemClick } = props;

  return (
    <List component={"nav" as any} classes={{ root: classes.root }}>
      {items.map((item, i) => {
        const isOpen = openedItems.includes(item.path);

        const arrow = Array.isArray(item.children) ? (
          isOpen ? (
            <ExpandLess />
          ) : (
            <ExpandMore />
          )
        ) : null;

        const linkProps = item.path
          ? { to: item.path, component: NavLink, activeClassName: "active" }
          : {};

        return (
          <React.Fragment key={item.path ?? i}>
            {item.subtitle && (
              <ListSubheader className={cns(classes.subtitle, "sidebar-hide-collapsed")} disableSticky>
                {item.subtitle}
              </ListSubheader>
            )}
            <ListItem
              key={`${item.path}_item`}
              {...linkProps}
              button
              className={cns(
                classes.listItem,
                item.indented && classes.indented,
                item.indented && "sidebar-no-indent-collapsed",
                item.hiddenInProduction && classes.hiddenInProduction,
                {
                  active: item.active
                }
              )}
              data-test={`main-menu-${item.path}`}
              onClick={() => onItemClick(item)}
            >
              {Boolean(item.badgeContent) && (
                <Badge
                  color={"primary"}
                  className={classes.notifBadge}
                  badgeContent={item.badgeContent === true ? "" : item.badgeContent}
                  data-test="Badge"
                  data-test-value={item.badgeContent}
                  variant="dot"
                >
                  <ListItemIcon style={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                </Badge>
              )}

              {!item.badgeContent && (
                <ListItemIcon style={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
              )}

              <ListItemText primary={item.label} className={classes.listItemText} />
              <IconButton
                size="small"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  toggle(item.path);
                }}
              >
                {arrow}
              </IconButton>
            </ListItem>

            {Array.isArray(item.children) && (
              <SubMenu
                key={`${item.path}_sub`}
                icon={item.icon}
                name={item.label}
                onClick={(subItem) => onItemClick(subItem)}
                items={item.children}
                open={isOpen}
              />
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  ...mixins.withListItem(theme),

  listItem: {
    ...mixins.listItem(theme),
    paddingTop: 6,
    paddingBottom: 6
  },

  root: {
    paddingTop: 0
  },

  notifBadge: {
    "& span": {
      color: theme.palette.common.white,
      fontWeight: theme.fontWeight.xBold,
      right: 10
    }
  },
  hiddenInProduction: {
    opacity: 0.2
  },
  indented: {
    paddingLeft: 32
  },
  subtitle: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    color: theme.palette.text.secondary,
    lineHeight: 1,
    padding: "16px 16px 4px"
  }
}));
